import { useCallback } from 'react';
import { useSubscribe } from '@mentimeter/quiz';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import {
  GameStateEnums,
  type InitialGameStates,
  PRESENTER_CHANGED_PLAYER_NAME_EVENT,
} from '@mentimeter/quiz-types';
import { useTranslations } from 'next-intl';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import {
  QuizLeaderboardVisualization,
  NoResults,
} from './QuizLeaderboardVisualization';
import type { useLeaderboardResults } from './useLeaderboardResults';

const LEADERBOARD_THRESHOLD = 10;

export const FollowerPresentation = ({
  initialGameState,
  timeBasedScoring,
  slidePublicKey,
  isPreviewResults = false,
  mutateResults,
  leaderboardResults,
  shouldAnimate,
}: {
  initialGameState: InitialGameStates;
  timeBasedScoring: boolean;
  slidePublicKey: string;
  isPreviewResults?: boolean;
  mutateResults: QuizPlayersScoreData['mutate'];
  leaderboardResults: ReturnType<typeof useLeaderboardResults>;
  shouldAnimate: boolean;
}) => {
  const t = useTranslations('presenting.quiz');
  const numberOfPlayers = leaderboardResults?.length ?? 0;

  useSubscribe({
    event: PRESENTER_CHANGED_PLAYER_NAME_EVENT,
    callback: useCallback(() => void mutateResults(undefined), [mutateResults]),
  });

  if (
    !leaderboardResults ||
    (leaderboardResults && leaderboardResults.length === 0)
  ) {
    return (
      <CenteringContainer>
        <NoResults />
      </CenteringContainer>
    );
  }

  if (isPreviewResults) {
    return (
      <CenteringContainer>
        <QuizLeaderboardVisualization
          id={`preview-results-question-${slidePublicKey}`}
          results={leaderboardResults}
          enableAnimations={shouldAnimate}
          timeBasedScoring={timeBasedScoring}
          revealWinner={initialGameState === GameStateEnums.FINAL_LEADERBOARD}
        />
      </CenteringContainer>
    );
  }

  return (
    <>
      <CenteringContainer>
        <QuizLeaderboardVisualization
          id={slidePublicKey}
          results={leaderboardResults}
          enableAnimations={shouldAnimate}
          timeBasedScoring={timeBasedScoring}
          revealWinner={initialGameState === GameStateEnums.FINAL_LEADERBOARD}
        />
      </CenteringContainer>
      {numberOfPlayers > LEADERBOARD_THRESHOLD && (
        <Text
          mb={5}
          textAlign="center"
          color="text"
          fontWeight="semiBold"
          fontSize="200"
        >
          {t('leaderboard.total_players_message', {
            numberOfPlayers,
          })}
        </Text>
      )}

      {/* Spacer for toolbar/controls */}
      <Box mt={5} />
    </>
  );
};
